import { lazy, ReactNode, Suspense, useEffect } from "react";
import { useSelector } from "react-redux";

import { Box, useBreakpoint } from "@seneca/senekit";

import { fetchCourseSectionsIfNotFetched } from "seneca-common/features/course/service";
import {
  useDispatch,
  useDispatchOnUnmount
} from "seneca-common/utils/hooks/dispatch";
import useScrollUpOnMount from "seneca-common/utils/hooks/scrolling/useScrollUpOnMount";
import useEffectOnUnmount from "seneca-common/utils/hooks/useEffectOnUnmount";
import { useMoment } from "seneca-common/utils/hooks/useMoment";
import loaderWithRetry from "seneca-common/utils/loaderWithRetry";

import fetchAllClassroomData from "features/classroom/actions/fetchAllClassroomData";
import Sidebar from "features/classroom/features/sidebar";
import SidebarContextProvider from "features/classroom/features/sidebar/SidebarContextProvider";
import registerModuleStaticInformationForClassroom from "features/classroom/routes/register-modules/static";
import {
  closeSidebar,
  getSidebarOpen,
  LearningMode,
  setLearningMode,
  setLearningModeMeta,
  toggleSidebar
} from "features/classroom/state";
import GeneralAppError from "features/common/components/AppErrors";
import ErrorHandler from "features/common/components/ErrorHandler";
import { LastLocation } from "features/common/routing/with-last-location";
import { isSchoolTeacherOrParent } from "features/user";

import ClassroomStructure from "../ClassroomStructure";
import useSetLearningMode from "./hooks/useSetLearningMode";
import useCloseSidebarOnWindowResizeIfNecessary from "./hooks/useToggleSidebarOnWindowResizeIfNecessary";

const Topbar = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "common-top-bar" */
        "features/common/top-bars/components/CommonTopbar"
      )
  )
);

type Props = {
  courseId: string;
  sectionId?: string;
  locationBeforeEnteringClassroom: LastLocation;
  children: ReactNode;
};

registerModuleStaticInformationForClassroom();

function Classroom({
  courseId,
  sectionId,
  locationBeforeEnteringClassroom,
  children
}: Props) {
  const now = useMoment();
  const dispatch = useDispatch();

  const sidebarOpen = useSelector(getSidebarOpen);

  useEffect(() => {
    dispatch(
      fetchAllClassroomData(courseId, now.subtract(2, "weeks").format())
    );
    dispatch(fetchCourseSectionsIfNotFetched(courseId));
  }, [dispatch, now, courseId]);

  useCloseSidebarOnWindowResizeIfNecessary();

  useSetLearningMode();

  useScrollUpOnMount();

  const { isDesktopOnly } = useBreakpoint();

  useEffectOnUnmount(() => {
    if (!isDesktopOnly) {
      dispatch(closeSidebar());
    }
  });
  useDispatchOnUnmount([
    setLearningMode(LearningMode.default),
    setLearningModeMeta({
      assignmentId: null,
      source: null
    })
  ]);

  const showTopBar = useSelector(isSchoolTeacherOrParent);

  return (
    <>
      <Suspense fallback={null}>
        {showTopBar && (
          <Topbar
            courseId={courseId}
            previousLocation={locationBeforeEnteringClassroom}
          />
        )}
      </Suspense>
      <ClassroomStructure
        courseId={courseId}
        drawerOpen={sidebarOpen}
        onOverlayClick={() => dispatch(toggleSidebar())}
        renderSidebar={() => (
          <SidebarContextProvider>
            <Sidebar courseId={courseId} activeSectionId={sectionId} />
          </SidebarContextProvider>
        )}
      >
        <Box width="100%">{children}</Box>
      </ClassroomStructure>
    </>
  );
}

export default ErrorHandler(Classroom, GeneralAppError);
